import React, { useCallback, useEffect, useState } from 'react';
import { Search, RefreshCw, IndianRupee, CreditCard, CheckCircle2 } from 'lucide-react';
import { useApi } from '../../lib/api';
import { getPlanFormErrors } from '../../lib/planFormValidation';

const fmtINR = (paise, currency = 'INR') =>
  (paise / 100).toLocaleString('en-IN', { style: 'currency', currency });

const blank = {
  code: '', name: '', description: '',
  price_paise: 0, currency: 'INR', interval: 'month', gst_percentage: 0,
  max_users: '',
  is_active: true, is_default: false,
};

export default function PlansPage() {
  const [tab, setTab] = useState('plans');

  return (
    <>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Plans &amp; Billing</h1>
          <p className="admin-page-subtitle">Plan catalog, tenant subscriptions, and all payments.</p>
        </div>
      </div>

      <div className="admin-tabs">
        <button className={`admin-tab${tab === 'plans'         ? ' active' : ''}`} onClick={() => setTab('plans')}>Plans</button>
        <button className={`admin-tab${tab === 'subscriptions' ? ' active' : ''}`} onClick={() => setTab('subscriptions')}>Subscriptions</button>
        <button className={`admin-tab${tab === 'payments'      ? ' active' : ''}`} onClick={() => setTab('payments')}>Payments</button>
      </div>

      {tab === 'plans'         && <PlansTab />}
      {tab === 'subscriptions' && <SubscriptionsTab />}
      {tab === 'payments'      && <PaymentsTab />}
    </>
  );
}

function PlansTab() {
  const api = useApi();
  const [plans, setPlans]               = useState([]);
  const [editing, setEditing]           = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [error, setError]               = useState(null);
  const [loading, setLoading]           = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.adminListPlans({ include_inactive: includeInactive ? 1 : 0 });
      setPlans(r.plans || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, includeInactive]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="admin-banner error">{error}</div>;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <label style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} />
          Show Inactive
        </label>
        <button className="btn-secondary" onClick={load} disabled={loading} style={{ gap: 6 }}>
          <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          Refresh
        </button>
        <button className="btn-primary" onClick={() => setEditing({ ...blank, _isNew: true })}>
          + New Plan
        </button>
      </div>

      <div className="admin-card">
        {plans.length === 0 ? (
          <div className="admin-empty">No plans found.</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>SL. NO</th>
                <th>CODE</th>
                <th>NAME</th>
                <th>PRICE / USER</th>
                <th>GST %</th>
                <th>INTERVAL</th>
                <th>MAX USERS</th>
                <th>ACTIVE</th>
                <th>DEFAULT</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.id}>
                  <td style={{ color: '#9ca3af', fontSize: 12 }}>{p.id}</td>
                  <td><code style={{ fontSize: 12, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, color: '#374151' }}>{p.code}</code></td>
                  <td style={{ fontWeight: 600, color: '#0f1c2e' }}>{p.name}</td>
                  <td style={{ fontWeight: 600 }}>{fmtINR(p.pricePaise, p.currency)} / user</td>
                  <td style={{ color: '#6b7280' }}>{p.gstPercentage ?? 0}%</td>
                  <td style={{ color: '#6b7280', textTransform: 'capitalize' }}>{p.interval}</td>
                  <td>{p.maxUsers == null ? '∞' : p.maxUsers}</td>
                  <td>{p.isActive ? <span className="admin-status-pill ok">Active</span> : <span className="admin-status-pill failed">Inactive</span>}</td>
                  <td>{p.isDefault ? <CheckCircle2 size={16} color="#22c55e" /> : <span style={{ color: '#d1d5db' }}>—</span>}</td>
                  <td>
                    <button style={{ font: 'inherit', fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
                      onClick={() => setEditing({ ...p })}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <PlanForm api={api} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </>
  );
}

function SubscriptionsTab() {
  const api = useApi();
  const [rows, setRows]     = useState([]);
  const [error, setError]   = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.adminListSubscriptions()
      .then(r => setRows(r.subscriptions || []))
      .catch(err => setError(err?.response?.data?.error || err.message));
  }, [api]);

  if (error) return <div className="admin-banner error">{error}</div>;

  const filtered = rows.filter(r =>
    !search ||
    r.app_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.plan_code?.toLowerCase().includes(search.toLowerCase()) ||
    r.plan_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="admin-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Subscriptions ({filtered.length})</h2>
        <div style={{ position: 'relative' }}>
          <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
          <input
            placeholder="Search tenant or plan..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoComplete="off"
            style={{ font: 'inherit', fontSize: 13, padding: '6px 12px 6px 30px', border: '1px solid #d1d5db', borderRadius: 8, width: 220, outline: 'none', color: '#374151' }}
          />
        </div>
      </div>
      {filtered.length === 0 ? <div className="admin-empty">No subscriptions found.</div> : (
        <div className="admin-table-scroll"><table className="admin-table">
          <thead>
            <tr>
              <th>SL. NO</th><th>TENANT</th><th>PLAN</th><th>AMOUNT/USER</th>
              <th>INTERVAL</th><th>MEMBERS</th><th>BILLING STATUS</th>
              <th>SUB STATUS</th><th>PERIOD START</th><th>PERIOD END</th><th>OWNER</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id}>
                <td style={{ color: '#9ca3af', fontSize: 12 }}>{s.id}</td>
                <td style={{ fontWeight: 600, color: '#0f1c2e' }}>{s.app_name}</td>
                <td>
                  <code style={{ fontSize: 12, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, color: '#374151' }}>{s.plan_code}</code>
                  <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 6 }}>{s.plan_name}</span>
                  {!s.plan_is_active && (
                    <span className="admin-status-pill failed" style={{ marginLeft: 6 }} title="This plan has since been deactivated — the tenant is still assigned to it">
                      Plan Deactivated
                    </span>
                  )}
                </td>
                <td>{s.price_paise != null ? fmtINR(s.price_paise, s.currency) : '—'}</td>
                <td style={{ color: '#6b7280', textTransform: 'capitalize' }}>{s.interval}</td>
                <td>{s.member_count}</td>
                <td><span className={`admin-status-pill ${pillFor(s.billing_status)}`}>{fmt(s.billing_status)}</span></td>
                <td><span className={`admin-status-pill ${pillFor(s.status)}`}>{fmt(s.status)}</span></td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{(s.period_start || '').slice(0, 10)}</td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{(s.period_end   || '').slice(0, 10)}</td>
                <td style={{ fontSize: 12, color: '#6b7280' }}>{s.owner_email || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
    </div>
  );
}

function PaymentsTab() {
  const api = useApi();
  const [rows, setRows]     = useState([]);
  const [error, setError]   = useState(null);
  const [search, setSearch] = useState('');
  const [recordOpen, setRecordOpen] = useState(false);

  const load = useCallback(() => {
    return api.adminListPayments()
      .then(r => setRows(r.payments || []))
      .catch(err => setError(err?.response?.data?.error || err.message));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="admin-banner error">{error}</div>;

  const filtered = rows.filter(r =>
    !search ||
    r.app_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.razorpay_order_id?.toLowerCase().includes(search.toLowerCase()) ||
    r.razorpay_payment_id?.toLowerCase().includes(search.toLowerCase()) ||
    r.plan_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalCollected = rows
    .filter(r => r.internal_status === 'reconciled' || r.internal_status === 'success')
    .reduce((s, r) => s + (r.amount_paise || 0), 0);

  return (
    <>
      <div className="admin-kpi-row" style={{ marginBottom: 16 }}>
        <div className="admin-kpi">
          <div className="admin-kpi-icon blue"><IndianRupee size={18} /></div>
          <p className="admin-kpi-label">Total Collected</p>
          <p className="admin-kpi-value">{fmtINR(totalCollected)}</p>
        </div>
        <div className="admin-kpi">
          <div className="admin-kpi-icon purple"><CreditCard size={18} /></div>
          <p className="admin-kpi-label">Total Payments</p>
          <p className="admin-kpi-value">{rows.length}</p>
        </div>
        <div className="admin-kpi ok">
          <div className="admin-kpi-icon green"><CheckCircle2 size={18} /></div>
          <p className="admin-kpi-label">Successful</p>
          <p className="admin-kpi-value">{rows.filter(r => r.internal_status === 'reconciled' || r.internal_status === 'success').length}</p>
        </div>
        <div className="admin-kpi danger">
          <div className="admin-kpi-icon red"><CreditCard size={18} /></div>
          <p className="admin-kpi-label">Failed / Pending</p>
          <p className="admin-kpi-value">{rows.filter(r => r.internal_status === 'failed' || r.internal_status === 'pending').length}</p>
        </div>
      </div>

      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>All Payments ({filtered.length})</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
              <input
                placeholder="Search tenant, order ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoComplete="off"
                style={{ font: 'inherit', fontSize: 13, padding: '6px 12px 6px 30px', border: '1px solid #d1d5db', borderRadius: 8, width: 240, outline: 'none', color: '#374151' }}
              />
            </div>
            <button className="btn-primary" onClick={() => setRecordOpen(true)}>
              + Record Manual Payment
            </button>
          </div>
        </div>
        {filtered.length === 0 ? <div className="admin-empty">No payments found.</div> : (
          <div className="admin-table-scroll"><table className="admin-table">
            <thead>
              <tr>
                <th>SL. NO</th><th>TENANT</th><th>PLAN</th><th>SEATS</th><th>AMOUNT</th>
                <th>STATUS</th><th>INTERNAL</th><th>RAZORPAY ORDER</th><th>PAYMENT ID</th><th>DATE</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id}>
                  <td style={{ color: '#9ca3af', fontSize: 12 }}>{p.id}</td>
                  <td style={{ fontWeight: 600, color: '#0f1c2e' }}>{p.app_name}</td>
                  <td style={{ color: '#6b7280' }}>{p.plan_name || '—'}</td>
                  <td style={{ color: '#6b7280' }}>{p.member_count ?? '—'}</td>
                  <td style={{ fontWeight: 700 }}>{p.amount_paise != null ? fmtINR(p.amount_paise, p.currency) : '—'}</td>
                  <td><span className={`admin-status-pill ${payPillFor(p.status)}`}>{fmt(p.status)}</span></td>
                  <td><span className={`admin-status-pill ${payPillFor(p.internal_status)}`}>{fmt(p.internal_status)}</span></td>
                  <td style={{ fontSize: 11, fontFamily: 'monospace', color: '#6b7280' }}>{p.razorpay_order_id || '—'}</td>
                  <td style={{ fontSize: 11, fontFamily: 'monospace', color: '#6b7280' }}>{p.razorpay_payment_id || '—'}</td>
                  <td style={{ fontSize: 12, color: '#6b7280' }}>{(p.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {recordOpen && (
        <ManualPaymentForm
          api={api}
          onClose={() => setRecordOpen(false)}
          onSaved={() => { setRecordOpen(false); load(); }}
        />
      )}
    </>
  );
}

function ManualPaymentForm({ api, onClose, onSaved }) {
  const [apps, setApps]   = useState([]);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    app_id: '', plan_id: '', period_months: 1, member_count: '', amount_paise: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    Promise.all([api.adminListAppsAll(), api.adminListPlans({ include_inactive: 0 })])
      .then(([a, p]) => {
        setApps(a.apps || []);
        setPlans(p.plans || []);
      })
      .catch(e => setErr(e?.response?.data?.error || e.message));
  }, [api]);

  const inputStyle = {
    font: 'inherit', fontSize: 13, padding: '8px 12px',
    border: '1px solid #d1d5db', borderRadius: 8,
    width: '100%', boxSizing: 'border-box', color: '#374151',
    background: '#ffffff', outline: 'none',
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.app_id || !form.plan_id) return;
    setBusy(true); setErr(null);
    try {
      await api.adminRecordManualPayment(form.app_id, {
        plan_id: Number(form.plan_id),
        period_months: Number(form.period_months) || 1,
        member_count: form.member_count === '' ? undefined : Number(form.member_count),
        amount_paise: form.amount_paise === '' ? undefined : Math.round(Number(form.amount_paise) * 100),
        notes: form.notes || undefined,
      });
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.error || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Record Manual Payment</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 0 }}>
            For payments collected outside Razorpay (bank transfer, cash, cheque). This
            activates the plan on the app immediately and counts toward the partner's
            commission if the app's owner has an active partner.
          </p>
          {err && <div className="admin-banner error">{err}</div>}
          <form onSubmit={submit}>
            <div className="form-row">
              <label>Tenant / App</label>
              <select style={inputStyle} value={form.app_id} onChange={e => setForm({ ...form, app_id: e.target.value })} required>
                <option value="">Select an app…</option>
                {apps.map(a => (
                  <option key={a.id} value={a.id}>{a.name} — {a.owner_email}</option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <label>Plan</label>
              <select style={inputStyle} value={form.plan_id} onChange={e => setForm({ ...form, plan_id: e.target.value })} required>
                <option value="">Select a plan…</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                ))}
              </select>
            </div>
            <div className="form-row" style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label>Period (months)</label>
                <input style={inputStyle} type="number" min={1} max={36} value={form.period_months} onChange={e => setForm({ ...form, period_months: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label>Seats (optional)</label>
                <input style={inputStyle} type="number" min={1} placeholder="Defaults to active members" value={form.member_count} onChange={e => setForm({ ...form, member_count: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <label>Amount collected in ₹ (optional)</label>
              <input style={inputStyle} type="number" min={0} step="0.01" placeholder="Defaults to plan price × seats × months" value={form.amount_paise} onChange={e => setForm({ ...form, amount_paise: e.target.value })} />
            </div>
            <div className="form-row">
              <label>Notes (optional)</label>
              <input style={inputStyle} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Bank transfer ref #1234" />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy || !form.app_id || !form.plan_id}>
                {busy ? 'Recording…' : 'Record Payment & Activate'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function PlanForm({ api, initial, onClose, onSaved }) {
  const isNew = initial._isNew;
  const [form, setForm] = useState({
    code:             initial.code             || '',
    name:             initial.name             || '',
    description:      initial.description      || '',
    price_paise:      initial.pricePaise       ?? initial.price_paise       ?? 0,
    currency:         initial.currency         || 'INR',
    interval:         initial.interval         || 'month',
    gst_percentage:   initial.gstPercentage    ?? initial.gst_percentage    ?? 0,
    max_users:        initial.maxUsers         ?? initial.max_users         ?? '',
    is_active:        initial.isActive         ?? initial.is_active         ?? true,
    is_default:       initial.isDefault        ?? initial.is_default        ?? false,
  });
  const [busy, setBusy]               = useState(false);
  const [err, setErr]                 = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const CODE_RE_FRONTEND = /^[a-zA-Z0-9_-]+$/;
  const codeError = isNew && form.code
    ? (form.code.length > 40 ? 'Plan code cannot exceed 40 characters'
      : !CODE_RE_FRONTEND.test(form.code) ? 'Plan code cannot contain special characters like #, $, @, etc.'
      : null)
    : null;

  const { priceError, maxUsersError, gstError } = getPlanFormErrors(form);

  const submit = async (e) => {
    e.preventDefault();
    if (codeError || priceError || maxUsersError || gstError) return;
    setBusy(true); setErr(null);
    const body = {
      ...(isNew ? { code: form.code || (() => {
        let slug = form.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
        slug = slug.replace(/^[^a-z]+/, '');
        if (slug.length < 2) slug = 'plan_' + (slug || 'default');
        return slug.slice(0, 40);
      })() } : {}),
      name:             form.name,
      description:      form.description || null,
      price_paise:      Number(form.price_paise),
      currency:         form.currency,
      interval:         form.interval,
      gst_percentage:   form.gst_percentage === '' ? 0 : Number(form.gst_percentage),
      max_users:        form.max_users === '' ? null : Number(form.max_users),
      is_active:        !!form.is_active,
      is_default:       !!form.is_default,
    };
    try {
      if (isNew) await api.adminCreatePlan(body);
      else       await api.adminUpdatePlan(initial.id, body);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    font: 'inherit', fontSize: 13, padding: '8px 12px',
    border: '1px solid #d1d5db', borderRadius: 8,
    width: '100%', boxSizing: 'border-box', color: '#374151',
    background: '#ffffff', outline: 'none',
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel modal-panel--lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isNew ? 'New Plan' : `Edit Plan: ${initial.name}`}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {err && <div className="admin-banner error">{err}</div>}
          <form onSubmit={submit}>
            <div className="form-row">
              <label>Plan Name</label>
              <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Starter Pack, Growth Edition" />
            </div>
            <div className="form-row">
              <label>Plan Description</label>
              <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Provide a brief summary of what is included in this plan..." />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 0.8fr 1fr 0.8fr', gap: 16, marginBottom: 16 }}>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label>Price Per User (₹)</label>
                <input
                  style={{ ...inputStyle, borderColor: priceError ? '#ef4444' : '#d1d5db' }}
                  type="number" min="0" step="0.01"
                  value={form.price_paise === '' ? '' : (Number(form.price_paise) / 100)}
                  onChange={e => {
                    const rupees = e.target.value;
                    if (rupees === '') { setForm({ ...form, price_paise: '' }); return; }
                    // Round to the nearest paisa — avoids float artifacts like
                    // 199.1 * 100 = 19909.999999999998.
                    setForm({ ...form, price_paise: Math.round(Number(rupees) * 100) });
                  }}
                  placeholder="e.g. 199 or 199.50"
                  required
                />
                {priceError && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{priceError}</div>}
              </div>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label>Currency</label>
                <input style={inputStyle} value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value.toUpperCase() })} maxLength={3} />
              </div>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label>Billing Interval</label>
                <select style={{ ...inputStyle, appearance: 'none', background: `#ffffff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%239ca3af' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 10px center`, cursor: 'pointer' }}
                  value={form.interval} onChange={e => setForm({ ...form, interval: e.target.value })}>
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                  <option value="one_time">One Time</option>
                </select>
              </div>
              <div className="form-row" style={{ marginBottom: 0 }}>
                <label>GST %</label>
                <input style={{ ...inputStyle, borderColor: gstError ? '#ef4444' : '#d1d5db' }} type="number" min="0" max="100" step="0.01" value={form.gst_percentage} onChange={e => setForm({ ...form, gst_percentage: e.target.value })} placeholder="e.g. 18" />
                {gstError && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{gstError}</div>}
              </div>
            </div>

            {/* Advanced settings */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginTop: 8 }}>
              <button type="button" onClick={() => setShowAdvanced(!showAdvanced)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#f9fafb', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#6b7280', textAlign: 'left' }}>
                <span>⚙ Advanced Settings <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(Optional)</span></span>
                <span style={{ fontSize: 11, transform: showAdvanced ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform 0.2s' }}>▶</span>
              </button>
              {(showAdvanced || maxUsersError) && (
                <div style={{ padding: 16, borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {isNew && (
                    <div className="form-row" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Plan Code <span style={{ fontWeight: 400, color: '#9ca3af' }}>(auto-generated if blank)</span></span>
                        <span style={{ fontWeight: 400, color: form.code.length > 40 ? '#ef4444' : '#9ca3af' }}>{form.code.length}/40</span>
                      </label>
                      <input
                        style={{ ...inputStyle, borderColor: codeError ? '#ef4444' : '#d1d5db' }}
                        value={form.code}
                        onChange={e => setForm({ ...form, code: e.target.value })}
                        placeholder="e.g. starter_pro"
                        maxLength={41}
                      />
                      {codeError && (
                        <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{codeError}</div>
                      )}
                      {!codeError && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Letters, numbers, _ and - only. Max 40 characters.</div>
                      )}
                    </div>
                  )}
                  <div className="form-row" style={{ marginBottom: 0 }}>
                    <label>Max Users <span style={{ fontWeight: 400, color: '#9ca3af' }}>(blank = unlimited)</span></label>
                    <input style={{ ...inputStyle, borderColor: maxUsersError ? '#ef4444' : '#d1d5db' }} type="number" min="0" value={form.max_users} onChange={e => setForm({ ...form, max_users: e.target.value })} placeholder="e.g. 10" />
                    {maxUsersError && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{maxUsersError}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                      Active (visible to tenants)
                    </label>
                    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#374151', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} />
                      Default for new signups
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy || !!codeError || !!priceError || !!maxUsersError || !!gstError}>{busy ? 'Saving…' : (isNew ? 'Create' : 'Save')}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function pillFor(s) {
  if (s === 'active')    return 'ok';
  if (s === 'trial')     return 'running';
  if (s === 'past_due')  return 'partial';
  if (s === 'suspended') return 'failed';
  if (s === 'cancelled') return 'failed';
  return 'skipped';
}

function payPillFor(s) {
  if (s === 'captured' || s === 'reconciled' || s === 'success') return 'ok';
  if (s === 'created'  || s === 'pending')   return 'running';
  if (s === 'failed')                        return 'failed';
  if (s === 'refunded')                      return 'partial';
  return 'skipped';
}

function fmt(s) {
  return (s || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '—';
}
